import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, ExternalLink, QrCode, FileText, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface FormsListProps {
  baseId?: string;
  tableId?: string;
}

export const FormsList = ({ baseId, tableId }: FormsListProps) => {
  const [forms, setForms] = useState<any[]>([]);
  const [tables, setTables] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (baseId) {
      fetchTables();
      fetchForms();
    }
  }, [baseId, tableId]);

  const fetchTables = async () => {
    try {
      const { data, error } = await supabase
        .from("tables")
        .select("*")
        .eq("base_id", baseId!)
        .order("created_at");

      if (error) throw error;
      setTables(data || []);
    } catch (error: any) {
      toast.error("Failed to load tables");
    }
  };

  const fetchForms = async () => {
    try {
      let query = supabase
        .from("forms")
        .select("*, tables(name)")
        .order("created_at", { ascending: false });

      if (tableId) {
        query = query.eq("table_id", tableId);
      } else if (baseId) {
        const { data: baseTables } = await supabase
          .from("tables")
          .select("id")
          .eq("base_id", baseId);
        
        if (baseTables) {
          const tableIds = baseTables.map(t => t.id);
          query = query.in("table_id", tableIds);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      setForms(data || []);
    } catch (error: any) {
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  const createForm = async () => {
    if (!tableId && tables.length === 0) {
      toast.error("Please create a table first");
      return;
    }

    try {
      const targetTableId = tableId || tables[0].id;
      const shareToken = crypto.randomUUID();
      
      const { data, error } = await supabase
        .from("forms")
        .insert([{
          table_id: targetTableId,
          name: "New Form",
          description: "",
          is_active: true,
          share_token: shareToken,
          config: {},
          whatsapp_flow: []
        }])
        .select()
        .single();

      if (error) throw error;
      navigate(`/form/${data.id}`);
    } catch (error: any) {
      toast.error("Failed to create form");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Loading forms...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Forms</h2>
          <p className="text-muted-foreground text-sm">Create forms to collect data via WhatsApp or web</p>
        </div>
        <Button onClick={createForm} className="gap-2">
          <Plus className="h-4 w-4" />
          New Form
        </Button>
      </div>

      {forms.length === 0 ? (
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="py-16 text-center">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No forms yet</h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
              Create a form to start collecting data. Forms can be shared via link, QR code, or embedded in websites.
            </p>
            <Button onClick={createForm} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => (
            <Card 
              key={form.id} 
              className="group hover:shadow-lg hover:border-primary/30 transition-all duration-200 cursor-pointer border-border/50"
              onClick={() => navigate(`/form/${form.id}`)}
            >
              <CardHeader className="p-4 pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold truncate group-hover:text-primary transition-colors">
                        {form.name}
                      </CardTitle>
                      <CardDescription className="text-xs truncate">
                        {form.tables?.name || "Unknown table"}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge 
                    variant="secondary" 
                    className={cn(
                      "text-xs flex-shrink-0",
                      form.is_active 
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" 
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {form.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <QrCode className="h-3.5 w-3.5" />
                      <span>QR</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Link2 className="h-3.5 w-3.5" />
                      <span>Link</span>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
